import {
	type GraphIssue,
	getPorts,
	NODE_CATEGORIES,
	NODE_TYPE_LABELS,
	type NodeType,
	summarizeNode,
} from "@velizhanin-dev/bot-scenario"
import { ChevronRight, CornerDownRight, Plus } from "lucide-react"
import { useMemo } from "react"
import type { DraftNode, ScenarioDraft } from "@/entities/bot-builder"
import {
	CATEGORY_APPEARANCE,
	edgeTargets,
	NODE_ICONS,
	orderFlowNodes,
	portKey,
} from "@/entities/bot-builder"
import { AddBlockMenu } from "@/features/edit-scenario-draft"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/ui/button"
import { StatusBadge } from "@/shared/ui/status-badge"

interface FlowListProps {
	draft: ScenarioDraft
	flowId: string
	issuesByNode: Map<string, GraphIssue[]>
	selectedNodeId: string | null
	activeNodeIds?: ReadonlySet<string>
	onSelectNode: (nodeId: string) => void
	/** Добавить блок и подключить его к свободному выходу `port` блока `nodeId`. */
	onAddFromPort: (nodeId: string, port: string, type: Exclude<NodeType, "start">) => void
}

/**
 * Поток списком: для телефона, где холст тесен, а провода пальцем не протянуть.
 * Под каждым блоком его выходы: куда ведут, по тапу: к следующему блоку; у свободного
 * выхода по тапу меню блоков, новый блок подключится к нему.
 */
export function FlowList({
	draft,
	flowId,
	issuesByNode,
	selectedNodeId,
	activeNodeIds,
	onSelectNode,
	onAddFromPort,
}: FlowListProps) {
	const { ordered, detached } = useMemo(() => orderFlowNodes(draft, flowId), [draft, flowId])
	const targets = useMemo(() => edgeTargets(draft.edges), [draft.edges])
	const nodeById = useMemo(() => new Map(draft.nodes.map((node) => [node.id, node])), [draft.nodes])

	const card = (node: DraftNode) => {
		const type = node.type as NodeType
		const appearance = CATEGORY_APPEARANCE[NODE_CATEGORIES[type] ?? "action"]
		const Icon = NODE_ICONS[type]
		const issues = issuesByNode.get(node.id) ?? []
		const errors = issues.filter((issue) => issue.severity === "error").length
		const ports = getPorts(node)
		return (
			<article
				key={node.id}
				className={cn(
					"overflow-hidden rounded-2xl border bg-card transition-shadow",
					node.id === selectedNodeId
						? "border-primary/70 ring-2 ring-primary/30"
						: errors > 0
							? "border-red-500/50"
							: "border-white/[0.08]",
					activeNodeIds?.has(node.id) && "shadow-[0_0_0_3px_rgba(52,211,153,0.5)]",
				)}
			>
				<Button
					type="button"
					variant="ghost"
					onClick={() => onSelectNode(node.id)}
					className="h-auto w-full items-start justify-start gap-3 rounded-none p-3 text-left hover:bg-transparent active:bg-accent/50"
				>
					<span
						className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-lg", appearance.tile)}
					>
						{Icon ? <Icon className="h-4 w-4" /> : null}
					</span>
					<span className="min-w-0 flex-1">
						<span className="flex items-center gap-2">
							<span className="truncate text-[13px] font-semibold">
								{NODE_TYPE_LABELS[type] ?? node.type}
							</span>
							{errors > 0 ? (
								<StatusBadge tone="danger" size="compact" className="bg-red-500/20">
									{errors}
								</StatusBadge>
							) : issues.length > 0 ? (
								<StatusBadge tone="warning" size="compact">
									{issues.length}
								</StatusBadge>
							) : null}
						</span>
						<span className="mt-0.5 line-clamp-2 block text-[13px] leading-snug text-foreground/75">
							{summarizeNode(node, draft.flows, draft.tags)}
						</span>
					</span>
					<ChevronRight className="mt-2 h-4 w-4 shrink-0 text-muted-foreground" />
				</Button>
				{ports.length > 0 ? (
					<div className="border-t border-white/[0.06] px-3 py-1">
						{ports.map((port) => {
							const target = nodeById.get(targets.get(portKey(node.id, port.id)) ?? "")
							const label = (
								<span className="max-w-[40%] shrink-0 truncate rounded-md bg-white/[0.05] px-1.5 py-0.5 text-muted-foreground">
									{port.label}
								</span>
							)
							if (!target) {
								return (
									<AddBlockMenu
										key={port.id}
										hint={`Новый блок подключится к выходу «${port.label}»`}
										onPick={(type) => onAddFromPort(node.id, port.id, type)}
										trigger={
											<Button
												type="button"
												variant="ghost"
												className="h-auto w-full min-w-0 justify-start gap-2 rounded-none py-1.5 text-left text-xs hover:bg-transparent active:bg-accent/50"
											>
												<CornerDownRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
												{label}
												<span className="text-amber-300/80">не подключено</span>
												<span className="ml-auto inline-flex items-center gap-1 text-muted-foreground">
													<Plus className="h-3.5 w-3.5" />
													блок
												</span>
											</Button>
										}
									/>
								)
							}
							return (
								<Button
									key={port.id}
									type="button"
									variant="ghost"
									onClick={() => onSelectNode(target.id)}
									className="h-auto w-full min-w-0 justify-start gap-2 rounded-none py-1.5 text-left text-xs hover:bg-transparent"
								>
									<CornerDownRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
									{label}
									<span className="truncate text-foreground/80">
										{NODE_TYPE_LABELS[target.type as NodeType] ?? target.type}:{" "}
										{summarizeNode(target, draft.flows, draft.tags)}
									</span>
								</Button>
							)
						})}
					</div>
				) : null}
			</article>
		)
	}

	return (
		<div className="h-full space-y-2 overflow-y-auto px-3 pt-16 pb-28">
			{ordered.map(card)}
			{detached.length > 0 ? (
				<>
					<p className="px-1 pt-4 text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
						Не связаны со стартом
					</p>
					{detached.map(card)}
				</>
			) : null}
		</div>
	)
}
